import type { Tenant, Post, Author, Media, Category } from '../../payload-types'
import { siteUrl, postUrl, categoryUrl, authorUrl, authorsUrl } from '../site'
import type { AuthorEntry } from '../authors'
import { clampDescription } from './meta'

const rel = (obj: unknown) => (obj && typeof obj === 'object' ? (obj as Record<string, unknown>) : null)

/**
 * URL del media. Esportato per og:image.
 *
 * IL FILE DEL DOC VINCE SULL'OVERRIDE. `bunnyUrl` è un override storico: serve ai media migrati da
 * WordPress, il cui `url` è rimasto nella forma PRE-Bunny (`/api/media/file/<filename>`) e la cui
 * immagine vive sul path legacy della pull zone. Per quei doc l'override è l'unico URL CDN che c'è,
 * quindi resta il fallback — ma quando `url` è già un URL assoluto (ogni media caricato dopo il
 * cutover, e ogni media a cui si sostituisce il file) quello è la verità e l'override non deve poter
 * coprirlo: se lo copre, un'immagine sostituita a mano resta invisibile in pagina mentre admin,
 * derivate `sizes` e og:image mostrano quella nuova. È accaduto, e da fuori sembra una cache che non
 * si svuota. Vedi l'hook beforeChange in src/collections/Media.ts, che tiene pulito il dato: i
 * media legacy sono stati portati sull'URL canonico nell'agosto 2026 e l'override è sparito.
 */
export const mediaUrl = (m: unknown): string | undefined => {
  const media = rel(m) as Media | null
  const own = typeof media?.url === 'string' ? media.url : ''
  if (own.startsWith('http')) return own
  return (media?.bunnyUrl as string) || own || undefined
}

/** Variante 1.91:1 destinata alle card social, con fallback all'originale per i media storici. */
export const socialImage = (m: unknown): { url: string; width?: number; height?: number; alt?: string } | undefined => {
  const media = rel(m) as Media | null
  if (!media) return undefined
  const og = media.sizes?.og
  const url = (og?.url as string) || mediaUrl(media)
  if (!url) return undefined
  return {
    url,
    width: (og?.width as number) || (media.width as number) || undefined,
    height: (og?.height as number) || (media.height as number) || undefined,
    alt: media.alt || undefined,
  }
}

/**
 * L'anteprima social delle pagine che un'immagine propria non ce l'hanno (home, categorie, tag e il
 * default del layout): `brand.socialImage` nella variante 1200x630, se il sito l'ha caricata;
 * altrimenti il logo, cioè quello che queste pagine mostravano prima che il campo esistesse.
 *
 * Il logo è il ripiego e non la regola perché è quadrato o trasparente: Facebook lo ritaglia a
 * 1,91:1 e X a 2:1, e un marchio tagliato sopra e sotto è la prima cosa che si vede quando qualcuno
 * condivide la home di un sito appena nato. Il ripiego porta solo l'URL, esattamente come prima:
 * dichiarare in og:image:width le misure di un quadrato non lo renderebbe meno ritagliato.
 */
export const brandShareImage = (
  tenant: Pick<Tenant, 'brand'>,
): { url: string; width?: number; height?: number; alt?: string } | undefined => {
  const card = socialImage(tenant.brand?.socialImage)
  if (card) return card
  const logo = mediaUrl(tenant.brand?.logo)
  return logo ? { url: logo } : undefined
}

/**
 * Variante PNG piccola per le EMAIL (`sizes.email-logo`). Nessun fallback all'originale, al
 * contrario di socialImage: l'originale è WebP, e un logo che su Outlook si vede come icona rotta
 * è peggio del nome della testata scritto in tipografia, che è quello che fa il guscio quando qui
 * non arriva niente.
 */
export const emailLogoUrl = (m: unknown): string | undefined => {
  const media = rel(m) as Media | null
  const url = media?.sizes?.['email-logo']?.url
  return typeof url === 'string' && url.length > 0 ? url : undefined
}

/** Normalizza una data a ISO 8601; undefined se assente/invalida. */
const iso = (v: unknown): string | undefined => {
  if (!v) return undefined
  const d = new Date(v as string | number | Date)
  return isNaN(d.getTime()) ? undefined : d.toISOString()
}

/**
 * Serializza JSON-LD per l'iniezione SSR in modo SICURO: neutralizza `</script>`,
 * `&` e i separatori di riga U+2028/U+2029 (XSS stored/SSR). Da usare SEMPRE al posto
 * di JSON.stringify dentro dangerouslySetInnerHTML.
 */
export const serializeJsonLd = (obj: unknown): string =>
  JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')

/**
 * Grafo JSON-LD unico per la pagina articolo, con relazioni @id, replica della
 * struttura @graph di Yoast: Article, WebPage, ImageObject, BreadcrumbList,
 * WebSite (+SearchAction), Organization, Person.
 *
 * INVARIANTE, e non è una scelta di implementazione ma un vincolo di rischio:
 *
 *   IL GRAFO NON LEGGE MAI IL CORPO DELL'ARTICOLO, NÉ ALCUN CAMPO CHE UN LLM POSSA SCRIVERE
 *   IN FORMA LIBERA, E NON EMETTE NESSUN NODO DI LUOGO.
 *
 * Vietati a qualunque condizione: Place, TouristAttraction, LocalBusiness, offers, price,
 * isAccessibleForFree, openingHoursSpecification, geo, latitude/longitude, address.
 *
 * Perché. Durante l'audit un agente dava per accertato «ingresso 1,00 euro» e voleva emetterlo
 * come `offers.price`: il grep sui nove documenti reali dava ZERO occorrenze di «€», «euro»,
 * «ingresso», «biglietto», e l'unica attestazione erano recensioni utente del 2017-2019 con una
 * variante concorrente («se non ricordiamo male sono 2 euro»). Emetterlo avrebbe reso questo sito
 * IL PRIMO POSTO SUL WEB in cui quel dato esiste in forma leggibile dalle macchine: non ripetere
 * un errore, generarlo. In un dato strutturato l'errore è spam macchina-leggibile, che è peggio
 * del thin content, e circola senza il contesto che lo smentisce.
 *
 * Il grafo povero è l'unica ragione per cui il claim falso di accessibilità non è diventato dato
 * strutturato: va protetto per costruzione, non per fortuna. Lo asserisce jsonld.test.ts.
 * Si rinuncia deliberatamente ai rich result per le attrazioni.
 */
export function buildArticleGraph(tenant: Tenant, post: Post) {
  const base = siteUrl(tenant)
  const pageUrl = postUrl(tenant, post.slug)
  const orgId = `${base}/#organization`
  const websiteId = `${base}/#website`
  const webpageId = `${pageUrl}#webpage`
  const articleId = `${pageUrl}#article`
  const imageId = `${pageUrl}#primaryimage`

  const author = rel(post.author) as Author | null
  const authorId = author ? personId(tenant, author) : undefined
  // Stessa catena di fallback dei meta social (page.tsx: seo.ogImage || featuredImage): og:image
  // e ImageObject del grafo devono indicare la STESSA immagine. Finché questo leggeva solo
  // featuredImage, un articolo impaginato senza testata ma con ogImage usciva con la card social
  // giusta e il JSON-LD senza immagine (successo sul post 69, 2026-08-23).
  const shareImage = post.seo?.ogImage ?? post.featuredImage
  const img = mediaUrl(shareImage)
  const orgType = tenant.brand?.organizationType || 'Organization'
  // freshness: ricade su publishedAt, MAI su updatedAt (che cambia a ogni save tecnico)
  const published = iso(post.publishedAt)
  const modifiedRaw = iso(post.contentUpdatedAt) ?? published
  // dateModified non può precedere datePublished (sarebbe invalido per schema.org)
  const modified = modifiedRaw && published && modifiedRaw < published ? published : modifiedRaw
  // categoria per breadcrumb e articleSection: primaryCategory, fallback alla prima
  const primaryCat = rel(post.seo?.primaryCategory) as Category | null
  const firstCat =
    Array.isArray(post.categories) && post.categories[0] && typeof post.categories[0] === 'object'
      ? (post.categories[0] as Category)
      : null
  const sectionCat = primaryCat ?? firstCat
  // `citation`: gli URL del blocco FONTI, che sono gia in pagina come link. Dichiararli nel grafo
  // dice a un motore da dove vengono i numeri dell'articolo, che per un pezzo di finanza e' il
  // segnale di attendibilita' piu' forte che abbiamo. L'ordine e' quello delle fonti (che e' la
  // numerazione delle citazioni), i doppioni si tolgono, il tetto a 25 evita un grafo gonfio.
  const citation = Array.from(
    new Set(
      (Array.isArray(post.sources) ? post.sources : [])
        .map((s) => (s && typeof s === 'object' ? String((s as { url?: unknown }).url ?? '') : ''))
        .filter((u) => u.startsWith('http')),
    ),
  ).slice(0, 25)
  // keywords: schema.org le vuole come lista o come stringa separata da virgole, che e' gia il
  // formato del campo in admin. Si passa solo se il redattore l'ha compilato.
  const keywords = (post.seo?.keywords || '').trim()

  const graph: Record<string, unknown>[] = []

  graph.push({
    '@type': 'Article',
    '@id': articleId,
    isPartOf: { '@id': webpageId },
    headline: (post.title || '').slice(0, 110),
    datePublished: published,
    dateModified: modified,
    ...(authorId ? { author: { '@id': authorId } } : {}),
    ...(sectionCat ? { articleSection: sectionCat.name } : {}),
    // description sull'Article e non solo sulla WebPage: e' la stessa frase della meta description,
    // ed e' il campo che un motore legge quando riassume l'articolo invece della pagina.
    description: clampDescription(post.seo?.metaDescription || post.excerpt),
    ...(keywords ? { keywords } : {}),
    ...(citation.length ? { citation } : {}),
    publisher: { '@id': orgId },
    mainEntityOfPage: { '@id': webpageId },
    ...(img ? { image: { '@id': imageId } } : {}),
    inLanguage: post.locale,
  })

  graph.push({
    '@type': 'WebPage',
    '@id': webpageId,
    url: pageUrl,
    name: post.seo?.title || post.title,
    isPartOf: { '@id': websiteId },
    ...(img ? { primaryImageOfPage: { '@id': imageId }, image: { '@id': imageId } } : {}),
    datePublished: published,
    dateModified: modified,
    description: clampDescription(post.seo?.metaDescription || post.excerpt),
    breadcrumb: { '@id': `${pageUrl}#breadcrumb` },
    inLanguage: post.locale,
  })

  if (img) {
    graph.push({
      '@type': 'ImageObject',
      '@id': imageId,
      url: img,
      contentUrl: img,
      caption: (rel(shareImage) as Media | null)?.alt || undefined,
    })
  }

  const crumbs: Record<string, unknown>[] = [{ '@type': 'ListItem', position: 1, name: 'Home', item: base }]
  if (primaryCat) {
    crumbs.push({ '@type': 'ListItem', position: 2, name: primaryCat.name, item: categoryUrl(tenant, primaryCat.slug) })
  }
  crumbs.push({ '@type': 'ListItem', position: crumbs.length + 1, name: post.title })
  graph.push({ '@type': 'BreadcrumbList', '@id': `${pageUrl}#breadcrumb`, itemListElement: crumbs })

  graph.push({
    '@type': 'WebSite',
    '@id': websiteId,
    url: base,
    name: tenant.brand?.siteName || tenant.name,
    publisher: { '@id': orgId },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${base}/search?q={search_term_string}` },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: tenant.locale,
  })

  const logo = mediaUrl(tenant.brand?.logo)
  graph.push({
    '@type': orgType,
    '@id': orgId,
    name: tenant.brand?.siteName || tenant.name,
    url: base,
    ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
    ...(Array.isArray(tenant.brand?.social) && tenant.brand.social.length
      ? { sameAs: tenant.brand.social.map((s) => s.url).filter(Boolean) }
      : {}),
  })

  // Il nodo Person solo per una firma documentata: la regola, e il perché, stanno in personNode.
  const person = author ? personNode(tenant, author) : null
  if (person) graph.push(person)

  return { '@context': 'https://schema.org', '@graph': graph }
}

/** L'`@id` della persona dietro una firma: lo stesso in ogni grafo che la nomina. */
const personId = (tenant: Pick<Tenant, 'primaryDomain'>, author: Pick<Author, 'id'>): string =>
  `${siteUrl(tenant)}/#/schema/person/${author.id}`

/**
 * Il nodo Person di una firma, o null. Lo emettono due grafi, l'articolo e l'indice /autori/, e la
 * definizione è una sola perché devono dichiarare LA STESSA persona: stesso `@id`, stessi campi.
 * Se divergessero, per un motore sarebbero due entità con lo stesso nome.
 *
 * Il nodo si emette SOLO se dietro c'è una persona documentata.
 *
 * Asserire in modo leggibile dalle macchine che un testo ha un autore, quando la scheda di
 * quell'autore è vuota (bio NULL su tutti e cinque gli autori del sito, uno dei quali è un
 * nickname), significa attribuire a una persona nominata un testo che non ha letto: è l'unico
 * rischio di questo dossier che ricade su un individuo invece che su un dominio. La byline resta
 * in pagina; ciò che si toglie è la sua affermazione nel grafo finché non è vera.
 */
export function personNode(tenant: Pick<Tenant, 'primaryDomain'>, author: Author): Record<string, unknown> | null {
  if (!author.bio) return null
  return {
    '@type': 'Person',
    '@id': personId(tenant, author),
    name: author.name,
    description: author.bio,
    ...(mediaUrl(author.avatar) ? { image: mediaUrl(author.avatar) } : {}),
    ...(Array.isArray(author.social) && author.social.length
      ? { sameAs: author.social.map((s) => s.url).filter(Boolean) }
      : {}),
  }
}

/**
 * Grafo JSON-LD per le pagine archivio (categoria/tag/autore, indice degli autori): CollectionPage
 * + breadcrumb. `mainEntity` e `nodes` sono per l'indice, che dichiara anche CHI elenca.
 */
export function buildCollectionGraph(
  tenant: Tenant,
  opts: {
    url: string
    name: string
    description?: string
    /** Che cosa la pagina elenca, sul CollectionPage (l'ItemList dell'indice degli autori). */
    mainEntity?: Record<string, unknown>
    /** Nodi in coda al grafo, referenziati per `@id` da `mainEntity`. */
    nodes?: Record<string, unknown>[]
  },
) {
  const base = siteUrl(tenant)
  const orgId = `${base}/#organization`
  const websiteId = `${base}/#website`
  const description = clampDescription(opts.description)
  const logo = mediaUrl(tenant.brand?.logo)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `${opts.url}#webpage`,
        url: opts.url,
        name: opts.name,
        ...(description ? { description } : {}),
        isPartOf: { '@id': websiteId },
        breadcrumb: { '@id': `${opts.url}#breadcrumb` },
        ...(opts.mainEntity ? { mainEntity: opts.mainEntity } : {}),
        inLanguage: tenant.locale,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${opts.url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: base },
          { '@type': 'ListItem', position: 2, name: opts.name },
        ],
      },
      {
        '@type': 'WebSite',
        '@id': websiteId,
        url: base,
        name: tenant.brand?.siteName || tenant.name,
        publisher: { '@id': orgId },
        inLanguage: tenant.locale,
      },
      {
        '@type': tenant.brand?.organizationType || 'Organization',
        '@id': orgId,
        name: tenant.brand?.siteName || tenant.name,
        url: base,
        ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
        ...(Array.isArray(tenant.brand?.social) && tenant.brand.social.length
          ? { sameAs: tenant.brand.social.map((s) => s.url).filter(Boolean) }
          : {}),
      },
      ...(opts.nodes ?? []),
    ],
  }
}

/**
 * Grafo dell'indice /autori/: il CollectionPage degli archivi, con le firme come ItemList
 * (`mainEntity`) e un nodo Person per ciascuna firma documentata (personNode, la stessa regola
 * dell'articolo).
 *
 * L'ELENCO DEL GRAFO È QUELLO IN PAGINA, voce per voce: stesso ordine, e l'`url` del profilo solo
 * dove la pagina lo linka, cioè per chi ha almeno un articolo pubblicato. Il profilo di chi non ne
 * ha è un archivio vuoto, e dichiararlo qui sarebbe indicare a un motore una pagina che il lettore
 * non vede linkata. Chi ha un nodo Person è legato a quello per `@id`, chi non lo ha resta un nome.
 */
export function buildAuthorsGraph(tenant: Tenant, entries: AuthorEntry[], description?: string) {
  const rows = entries.map((entry) => ({ ...entry, person: personNode(tenant, entry.author) }))
  return buildCollectionGraph(tenant, {
    url: authorsUrl(tenant),
    name: 'Autori',
    description,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: rows.length,
      itemListElement: rows.map(({ author, published, person }, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: author.name,
        ...(published > 0 ? { url: authorUrl(tenant, author.slug) } : {}),
        ...(person ? { item: { '@id': person['@id'] } } : {}),
      })),
    },
    nodes: rows.flatMap(({ person }) => (person ? [person] : [])),
  })
}

/** Grafo JSON-LD per la homepage: WebSite (+SearchAction) + Organization. */
export function buildHomeGraph(tenant: Tenant) {
  const base = siteUrl(tenant)
  const orgId = `${base}/#organization`
  const websiteId = `${base}/#website`
  const logo = mediaUrl(tenant.brand?.logo)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': websiteId,
        url: base,
        name: tenant.brand?.siteName || tenant.name,
        ...(tenant.brand?.tagline ? { description: tenant.brand.tagline } : {}),
        publisher: { '@id': orgId },
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${base}/search?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
        inLanguage: tenant.locale,
      },
      {
        '@type': tenant.brand?.organizationType || 'Organization',
        '@id': orgId,
        name: tenant.brand?.siteName || tenant.name,
        url: base,
        ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
        ...(Array.isArray(tenant.brand?.social) && tenant.brand.social.length
          ? { sameAs: tenant.brand.social.map((s) => s.url).filter(Boolean) }
          : {}),
      },
    ],
  }
}
